import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, ListGroup, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import { useNavigate } from "react-router-dom";
// small lookups used for selects (you can replace with dynamic lookups if you want)
const DURATION_UNITS = ["H", "Days", "Weeks", "Months", "Years"];
const MEASURE_UNITS = ["EA", "KG", "NOS", "L"];
const LABOUR_TYPES = ["SUP", "MAN", "LAB"];
const LABOUR_NAMES = ["Supervisor", "Manager", "Labour"];

export default function CreateProductionOrder() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editableOutput, setEditableOutput] = useState([]);
  const [editableSequence, setEditableSequence] = useState([]);
  const [editableLabour, setEditableLabour] = useState([]);
  const [editableBOM, setEditableBOM] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingLookups, setFetchingLookups] = useState(false);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const { user } = useAuth();
  // items lookups:
  const [itemsLookup, setItemsLookup] = useState([]); // RA* items for BOM
  const [allItemsLookup, setAllItemsLookup] = useState([]); // all items for SFG & name lookup

  // process & subprocess lookup maps
  const [processMap, setProcessMap] = useState({});
  const [subprocessMap, setSubprocessMap] = useState({});

  // fixed output item id (the output item is not editable)
  const [fixedOutputItemId, setFixedOutputItemId] = useState("");

  useEffect(() => {
    if (!planId) return;
    fetchData();
    fetchItemsLookup();
    fetchUnits();
    // eslint-disable-next-line
  }, [planId]);

  // fetch processes when user/org is available
  useEffect(() => {
    if (!user?.organisationId) return;
    fetchProcesses(user.organisationId);
    fetchSubProcesses(user.organisationId);
    // eslint-disable-next-line
  }, [user?.organisationId]);

  const fetchUnits = async () => {
    try {
      const res = await axios.get(`${Base_URL}/UnitMaster/GetAllUnits`);
      const arr = Array.isArray(res?.data) ? res.data : res?.data ?? [];
      setUnits(arr);
    } catch (err) {
      console.warn("Failed fetching units", err);
      setUnits([]);
    }
  };

  const fetchProcesses = async (orgId) => {
    try {
      const url = `${Base_URL}/ProductionStation/GetAllProcesses/id?id=${encodeURIComponent(orgId)}`;
      const res = await axios.get(url);
      const arr = Array.isArray(res?.data) ? res.data : [];
      const map = {};
      arr.forEach(p => {
        if (p?.productionProcessId) map[String(p.productionProcessId)] = p.productionProcessDesc ?? "";
      });
      setProcessMap(map);
    } catch (err) {
      console.error("Failed fetching processes", err);
      setProcessMap({});
    }
  };

  // Try organisation-aware subprocess endpoint first; fallback to base endpoint
  const fetchSubProcesses = async (orgId) => {
    try {
      // try endpoint with org id param
      let res;
      try {
        res = await axios.get(`${Base_URL}/ProductionStation/GetAllSubProcesses/id?id=${encodeURIComponent(orgId)}`);
      } catch (e) {
        // fallback to non-id endpoint
        res = await axios.get(`${Base_URL}/ProductionStation/GetAllSubProcesses`);
      }
      const arr = Array.isArray(res?.data) ? res.data : [];
      const map = {};
      arr.forEach(sp => {
        if (sp?.productionSubprocessId) map[String(sp.productionSubprocessId)] = sp.productionSubprocessDesc ?? "";
      });
      setSubprocessMap(map);
    } catch (err) {
      console.error("Failed fetching subprocesses", err);
      setSubprocessMap({});
    }
  };

  const fetchItemsLookup = async () => {
    try {
      setFetchingLookups(true);
      const res = await fetch(`${Base_URL}/Item/ItemGetData`);
      if (!res.ok) throw new Error(`Failed to load items: ${res.status}`);
      const iData = await res.json();
      const arr = Array.isArray(iData) ? iData : [];
      setAllItemsLookup(arr);
      const filtered = arr.filter(it => (it.itemCode && String(it.itemCode).startsWith("RA")));
      setItemsLookup(filtered);
    } catch (err) {
      console.warn("Failed fetching items lookup", err);
      setItemsLookup([]);
      setAllItemsLookup([]);
    } finally {
      setFetchingLookups(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${Base_URL}/ProductionOrder/dataForProductionOrder/orderId?orderId=${encodeURIComponent(planId)}`;
      const res = await axios.get(url);
      const payload = res.data?.result ?? res.data ?? null;
      setData(payload);

      // determine fixed output item id
      const fixedId = payload?.output?.[0]?.itemCodeId ?? "";
      setFixedOutputItemId(fixedId);

      // init editable copies
      setEditableOutput((payload?.output || []).map(o => ({
        itemCodeId: fixedId || o.itemCodeId || "",
        productionQty: o.productionQty ?? "",
        productionQtyUnit: o.productionQtyUnit ?? MEASURE_UNITS[0],
      })));

      setEditableSequence((payload?.sequence || []).map(s => ({
        productionProcessId: s.productionProcessId ?? "",
        productionProcessSeq: s.productionProcessSeq ?? null,
        productionSubprocessId: s.productionSubprocessId ?? "",
        productionStationId: s.productionStationId ?? "",
        approxDuration: s.approxDuration ?? "",
        approxDurationUnit: s.approxDurationUnit ?? DURATION_UNITS[0],
        stgOutputItem: s.stgOutputItem ?? "",
        stgOutputQty: s.stgOutputQty ?? "",
        stgOutputUnit: s.stgOutputUnit ?? MEASURE_UNITS[0],
      })));

      setEditableLabour((payload?.labour || []).map(l => ({
        productionLabourType: l.productionLabourType ?? LABOUR_TYPES[0],
        approxLabourDuration: l.approxLabourDuration ?? "",
        approxDurationUnit: l.approxDurationUnit ?? DURATION_UNITS[0],
        productionProcessId: l.productionProcessId ?? "",
        productionSubprocessId: l.productionSubprocessId ?? "",
      })));

      setEditableBOM((payload?.bom || []).map(b => ({
        bomId: b.componentBomId ?? b?.bom?.bomId ?? b?.BOMId ?? b?.id ?? "",
        componentItemCodeId: b.componentItemCodeId ?? "",
        productionProcessId: b.productionProcessId ?? "",
        productionSubprocessId: b.productionSubprocessId ?? "",
        bomLoading: false,
        bomError: null,
        bomMeta: b.bom ?? b ?? null
      })));
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message ?? err.message ?? "Failed to fetch");
    } finally {
      setLoading(false);
    }
  };

  // helpers
  const totalItemsCount = Array.isArray(data?.items) ? data.items.length : 0;
  const availableItemsCount = Array.isArray(data?.avilableItems) ? data.avilableItems.length : 0;

  const originalAndAvailableMatch = () => {
    const orig = Array.isArray(data?.items) ? data.items.map(i => String(i.itemCodeId)) : [];
    const avail = Array.isArray(data?.avilableItems) ? data.avilableItems.map(i => String(i.itemCodeId)) : [];
    if (orig.length === 0) return false;
    if (orig.length !== avail.length) return false;
    const origSet = new Set(orig);
    for (const id of avail) if (!origSet.has(id)) return false;
    return true;
  };

  // fixed output label
  const fixedOutputItemLabel = () => {
    if (!fixedOutputItemId) return "";
    const it = allItemsLookup.find(x => String(x.itemCodeId) === String(fixedOutputItemId));
    return it ? `${it.itemCode} - ${it.itemName}` : String(fixedOutputItemId);
  };

  // utility label helpers
  const getProcessLabel = (processId) => {
    if (!processId) return "";
    const desc = processMap[String(processId)];
    return desc ? `${processId} - ${desc}` : String(processId);
  };
  const getSubprocessLabel = (subprocessId) => {
    if (!subprocessId) return "";
    const desc = subprocessMap[String(subprocessId)];
    return desc ? `${subprocessId} - ${desc}` : String(subprocessId);
  };

  // OUTPUT handlers
  const handleOutputChange = (index, field, value) => {
    setEditableOutput(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addOutput = () => {
    const defaultItem = fixedOutputItemId || (allItemsLookup[0]?.itemCodeId ?? "");
    const defaultUnit = MEASURE_UNITS[0];
    setEditableOutput(prev => [...prev, {
      itemCodeId: defaultItem,
      productionQty: "",
      productionQtyUnit: defaultUnit
    }]);
  };

  const removeOutput = (index) => {
    setEditableOutput(prev => prev.filter((_, i) => i !== index));
  };

  // SEQUENCE handlers
  const handleSequenceChange = (index, field, value) => {
    setEditableSequence(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const moveSequence = (index, dir) => {
    setEditableSequence(prev => {
      const arr = [...prev];
      const ni = index + dir;
      if (ni < 0 || ni >= arr.length) return arr;
      const t = arr[ni];
      arr[ni] = arr[index];
      arr[index] = t;
      return arr;
    });
  };

  // LABOUR handlers
  const handleLabourChange = (index, field, value) => {
    setEditableLabour(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addLabour = () => {
    setEditableLabour(prev => [...prev, {
      productionLabourType: LABOUR_TYPES[0],
      approxLabourDuration: "",
      approxDurationUnit: DURATION_UNITS[0],
      productionProcessId: "",
      productionSubprocessId: "",
    }]);
  };

  const removeLabour = (index) => {
    setEditableLabour(prev => prev.filter((_, i) => i !== index));
  };

  const assignLabourToSubprocess = (labIdx, subprocessId) => {
    handleLabourChange(labIdx, "productionSubprocessId", subprocessId);
    const seq = editableSequence.find(s => s.productionSubprocessId === subprocessId);
    if (seq) handleLabourChange(labIdx, "productionProcessId", seq.productionProcessId || "");
  };

  // BOM handlers & BOM fetch helpers are unchanged from previous
  const handleBOMChange = (index, field, value) => {
    setEditableBOM(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const extractBomIdFromResponse = (bomData) => {
    if (!bomData) return null;
    if (bomData.result) return extractBomIdFromResponse(bomData.result);
    if (Array.isArray(bomData)) return extractBomIdFromResponse(bomData[0]);
    const candidates = [
      bomData.bomId, bomData.BOMId, bomData.id, bomData.ID, bomData.Id, bomData.idBom, bomData.code
    ];
    for (const c of candidates) if (c != null && c !== "") return c;
    if (bomData.bom && typeof bomData.bom === "object") return extractBomIdFromResponse(bomData.bom);
    return null;
  };

  const onBOMComponentSelect = async (bomIdx, itemCodeId) => {
    setEditableBOM(prev => {
      const next = [...prev];
      if (!next[bomIdx]) {
        while (next.length <= bomIdx) {
          next.push({
            bomId: "",
            componentItemCodeId: "",
            productionProcessId: "",
            productionSubprocessId: "",
            bomLoading: false,
            bomError: null,
            bomMeta: null
          });
        }
      }
      next[bomIdx] = {
        ...next[bomIdx],
        componentItemCodeId: itemCodeId,
        bomLoading: !!itemCodeId,
        bomError: null,
        bomMeta: null,
        bomId: ""
      };
      return next;
    });

    if (!itemCodeId) {
      setEditableBOM(prev => {
        const next = [...prev];
        if (next[bomIdx]) next[bomIdx] = { ...next[bomIdx], bomLoading: false };
        return next;
      });
      return;
    }

    try {
      const res = await fetch(`${Base_URL}/ProductionStation/GetBOMfromItem/id?id=${encodeURIComponent(itemCodeId)}`);
      if (!res.ok) throw new Error(`BOM fetch failed: ${res.status}`);
      const bomDataRaw = await res.json();
      const bomData = bomDataRaw?.result ?? bomDataRaw;
      const bomIdFromResp = extractBomIdFromResponse(bomData);

      setEditableBOM(prev => {
        const next = [...prev];
        if (!next[bomIdx]) {
          next[bomIdx] = {
            bomId: bomIdFromResp ?? "",
            componentItemCodeId: itemCodeId,
            productionProcessId: "",
            productionSubprocessId: "",
            bomLoading: false,
            bomError: null,
            bomMeta: bomData ?? null
          };
        } else {
          next[bomIdx] = {
            ...next[bomIdx],
            bomLoading: false,
            bomError: null,
            bomMeta: bomData ?? null,
            bomId: bomIdFromResp ?? ""
          };
        }
        return next;
      });
    } catch (err) {
      console.error("Error fetching BOM for component item", err);
      setEditableBOM(prev => {
        const next = [...prev];
        if (!next[bomIdx]) {
          next[bomIdx] = {
            bomId: "",
            componentItemCodeId: itemCodeId,
            productionProcessId: "",
            productionSubprocessId: "",
            bomLoading: false,
            bomError: err.message ?? "Failed to load BOM",
            bomMeta: null
          };
        } else {
          next[bomIdx] = {
            ...next[bomIdx],
            bomLoading: false,
            bomError: err.message ?? "Failed to load BOM",
            bomMeta: null,
            bomId: ""
          };
        }
        return next;
      });
    }
  };

  const addBOM = () => {
    setEditableBOM(prev => {
      const next = [...prev, {
        bomId: "",
        componentItemCodeId: "",
        productionProcessId: "",
        productionSubprocessId: "",
        bomLoading: false,
        bomError: null,
        bomMeta: null
      }];

      const newIndex = next.length - 1;
      const defaultComponent = itemsLookup[0]?.itemCodeId ?? null;
      if (defaultComponent) {
        next[newIndex].componentItemCodeId = defaultComponent;
        next[newIndex].bomLoading = true;
        setTimeout(() => onBOMComponentSelect(newIndex, defaultComponent), 0);
      }

      return next;
    });
  };

  const removeBOM = (index) => {
    setEditableBOM(prev => prev.filter((_, i) => i !== index));
  };

  const assignBOMToSubprocess = (bomIdx, subprocessId) => {
    handleBOMChange(bomIdx, "productionSubprocessId", subprocessId);
    const seq = editableSequence.find(s => s.productionSubprocessId === subprocessId);
    if (seq) handleBOMChange(bomIdx, "productionProcessId", seq.productionProcessId || "");
  };

  // Build combined payload & log
  const handleSaveAll = async () => {
    const payload = {
      planId: planId,
      planDescription: data?.plan?.productionPlanDesc ?? "",
      organisationId: data?.plan?.organisationId ?? null,
      output: editableOutput.map(o => ({
        itemCodeId: o.itemCodeId,
        productionQty: Number(o.productionQty) || 0,
        productionQtyUnit: o.productionQtyUnit
      })),
      sequences: editableSequence.map((s, i) => ({
        sequenceNo: i + 1,
        productionProcessId: s.productionProcessId,
        productionSubprocessId: s.productionSubprocessId,
        productionStationId: s.productionStationId,
        approxDuration: Number(s.approxDuration) || 0,
        approxDurationUnit: s.approxDurationUnit,
        stgOutputItem: s.stgOutputItem,
        stgOutputQty: Number(s.stgOutputQty) || 0,
        stgOutputUnit: s.stgOutputUnit
      })),
      labour: editableLabour.map(l => ({
        productionLabourType: l.productionLabourType,
        approxLabourDuration: Number(l.approxLabourDuration) || 0,
        approxDurationUnit: l.approxDurationUnit,
        productionProcessId: l.productionProcessId,
        productionSubprocessId: l.productionSubprocessId
      })),
      bom: editableBOM.map(b => ({
        bomId: b.bomId || null,
        componentItemCodeId: b.componentItemCodeId,
        productionProcessId: b.productionProcessId,
        productionSubprocessId: b.productionSubprocessId,
        bomMeta: b.bomMeta ?? null
      })),
      summary: {
        totalItems: Array.isArray(data?.items) ? data.items.length : 0,
        availableItems: Array.isArray(data?.avilableItems) ? data.avilableItems.length : 0
      },
      originalOutput: data.output,
      originalPlan: data.plan,
      originalLabour: data.labour,
      createdBy: String(user?.userId)
    };

    try {
      const res = await axios.post(`${Base_URL}/ProductionOrder/createOrder/id?id=${planId}`, payload);
      navigate(`/executeProduction-order/${planId}`);
      console.log(res);
    } catch (err) {
      console.error("Error saving production order:", err);
      setError(err.response?.data?.message ?? err.message ?? "Save failed");
      return;
    }
    alert("Payload logged to console.");
  };

  return (
    <div style={{ minHeight: "20px", maxHeight: "20px" }}>
      <Container fluid className>
        <h3 className="fields-title">Production Order — {planId}</h3>

        <div className="mb-3 d-flex gap-2">
          <Button onClick={fetchData} disabled={loading || fetchingLookups} size="sm">Refresh</Button>
          <Button onClick={handleSaveAll} variant="primary" disabled={loading} size="sm">Execute Order</Button>
        </div>

        {loading && <div style={{ marginBottom: 12 }}><Spinner animation="border" /></div>}
        {error && <div style={{ color: "red", marginBottom: 12 }}>{error}</div>}

        <Row>
          {/* LEFT: Original, read-only details + Available Items */}
          <Col md={4}>
            <div className="border rounded p-3 mb-3">
              <h6>Original Plan Details</h6>
              <ListGroup variant="flush">
                <ListGroup.Item><strong>Production Plan ID:</strong> {data?.plan?.productionPlanId}</ListGroup.Item>
                <ListGroup.Item><strong>Description:</strong> {data?.plan?.productionPlanDesc}</ListGroup.Item>
                <ListGroup.Item><strong>Organisation:</strong> {data?.plan?.organisationId}</ListGroup.Item>
                <ListGroup.Item><strong>Office Code:</strong> {data?.plan?.officeCode}</ListGroup.Item>
              </ListGroup>
            </div>

            <div className="border rounded p-3 mb-3">
              <h6>Original Output</h6>
              {Array.isArray(data?.output) && data.output.length > 0 ? (
                <ListGroup>
                  {data.output.map(o => (
                    <ListGroup.Item key={o.itemCodeId}>
                      <div><strong>{o.itemCodeId}</strong></div>
                      <div className="small text-muted">Qty: {o.productionQty} {o.productionQtyUnit}</div>
                      <div className="small text-muted">{data?.outputItem?.itemName}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : <div className="text-muted">No output in original plan.</div>}
            </div>

            <div className="border rounded p-3 mb-3">
              <h6>Original BOM</h6>
              {Array.isArray(data?.bom) && data.bom.length > 0 ? (
                <ListGroup>
                  {data.bom.map(b => (
                    <ListGroup.Item key={b.bomId ?? b?.bom?.bomId ?? b.componentItemCodeId}>
                      <div><strong>{b.bomId ?? b?.bom?.bomId ?? "(unknown)"}</strong></div>
                      <div className="small text-muted">{b.parentItemCodeId ? `${b.parentItemCodeId} → ` : ""}{b.componentItemCodeId}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : <div className="text-muted">No BOM in original plan.</div>}
            </div>

            <div className="border rounded p-3 mb-3">
              <h6>Original Items</h6>
              {Array.isArray(data?.items) && data.items.length > 0 ? (
                <ListGroup>
                  {data.items.map(it => (
                    <ListGroup.Item key={it.itemCodeId}>
                      <div><strong>{it.itemName || it.itemCode}</strong></div>
                      <div className="small text-muted">{it.itemCodeId} • Unit: {it.skuUnit}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : <div className="text-muted">No items in original plan.</div>}
            </div>

            <div className="border rounded p-3 mb-3">
              <h6>Available Items</h6>
              {Array.isArray(data?.avilableItems) && data.avilableItems.length > 0 ? (
                <ListGroup>
                  {data.avilableItems.map(av => (
                    <ListGroup.Item key={av.itemCodeId} className="d-flex justify-content-between align-items-center">
                      <div>
                        <div><strong>{av.itemName || av.itemCode}</strong></div>
                        <div className="small text-muted">{av.itemCodeId} • Unit: {av.skuUnit}</div>
                      </div>
                      <div><span className="badge bg-success">In Stock</span></div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : <div className="text-muted">No available items.</div>}
            </div>

            {/* Execution message when available items == original items */}
            {originalAndAvailableMatch() && (
              <Alert variant="success">
                <strong>You can start with Execution</strong>
                <div className="small">Update inventory</div>
              </Alert>
            )}

            <div className="border rounded p-3 mb-3">
              <h6>Inventory</h6>
              {Array.isArray(data?.inventory) && data.inventory.length > 0 ? (
                <ListGroup>
                  {data.inventory.map(inv => (
                    <ListGroup.Item key={inv.id}>
                      <div><strong>{inv.itemCode}</strong></div>
                      <div className="small text-muted">Qty: {inv.itemQty} {inv.unit}</div>
                      <div className="small text-muted">Storage: {inv.storageCenter} • Office: {inv.officeCode}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : <div className="text-muted">No inventory records.</div>}
            </div>
          </Col>

          {/* RIGHT: Editable panels */}
          <Col md={8}>
            {/* Planned Output (item fixed) */}
            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Planned Output</h6>
                <div>
                  <Button size="sm" onClick={addOutput} disabled={!fixedOutputItemId}>+ Add Output</Button>
                </div>
              </div>

              {/* show the fixed output item label at top */}
              <div className="mb-2"><small className="text-muted">Output item:</small> <div><strong>{fixedOutputItemLabel() || "(not set)"}</strong></div></div>

              {editableOutput.length === 0 ? (
                <div className="text-muted">No planned outputs (add with the + button).</div>
              ) : (
                editableOutput.map((o, idx) => (
                  <Row key={`${o.itemCodeId || "out"}-${idx}`} className="align-items-end mb-2">
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label>Output Item</Form.Label>
                        <Form.Control type="text" readOnly value={fixedOutputItemLabel()} />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Qty</Form.Label>
                        <Form.Control type="number" step="any" value={o.productionQty} onChange={(e) => handleOutputChange(idx, "productionQty", e.target.value)} />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Unit</Form.Label>
                        <Form.Select value={o.productionQtyUnit} onChange={(e) => handleOutputChange(idx, "productionQtyUnit", e.target.value)}>
                          {units.map(u => <option key={u.code || u.unitCode || u.id} value={u.code ?? u.unitCode ?? u.id}>{(u.code ?? u.unitCode ?? u.id) + (u.description ? ` - ${u.description}` : "")}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={1} className="text-end">
                      <Button variant="outline-danger" onClick={() => removeOutput(idx)}>-</Button>
                    </Col>
                  </Row>
                ))
              )}
            </div>

            {/* Sequences */}
            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Sequences</h6>
                <small className="text-muted">Reorder / edit SFG & duration</small>
              </div>

              {editableSequence.length === 0 ? (
                <div className="text-muted">No sequences found.</div>
              ) : (
                editableSequence.map((s, idx) => (
                  <div key={`${s.productionProcessId || "seq"}-${idx}`} className="mb-3 p-2 border rounded">
                    <Row className="align-items-center">
                      <Col md={1}>
                        <div className="d-flex gap-1 justify-content-center">
                          <Button size="sm" onClick={() => moveSequence(idx, -1)} style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", lineHeight: 1, minWidth: "20px", maxWidth: "20px", height: "22px", padding: 0 }} title="Move up">↑</Button>
                          <Button size="sm" onClick={() => moveSequence(idx, 1)} style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", lineHeight: 1, minWidth: "20px", maxWidth: "20px", height: "22px", padding: 0 }} title="Move down">↓</Button>
                        </div>
                      </Col>

                      <Col md={7}>
                        {/* show processId - description and subprocess label */}
                        <div>
                          <strong>{getProcessLabel(s.productionProcessId)}</strong> • {getSubprocessLabel(s.productionSubprocessId)} <small className="text-muted">({s.productionStationId})</small>
                        </div>
                      </Col>

                      <Col md={4} className="text-end"><small className="text-muted">Step #{idx + 1}</small></Col>
                    </Row>

                    <Row className="mt-2">
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Approx Duration</Form.Label>
                          <Form.Control type="number" step="any" value={s.approxDuration} onChange={(e) => handleSequenceChange(idx, "approxDuration", e.target.value)} />
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Duration Unit</Form.Label>
                          <Form.Select value={s.approxDurationUnit} onChange={(e) => handleSequenceChange(idx, "approxDurationUnit", e.target.value)}>
                            {DURATION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>SFG Item</Form.Label>
                          <Form.Select value={s.stgOutputItem || ""} onChange={(e) => handleSequenceChange(idx, "stgOutputItem", e.target.value)} disabled={fetchingLookups}>
                            <option value="">Select SFG Item</option>
                            {allItemsLookup.map(it => (
                              <option key={it.itemCodeId} value={it.itemCodeId}>{it.itemCode} - {it.itemName}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>SFG Qty / Unit</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control type="number" step="any" value={s.stgOutputQty} onChange={(e) => handleSequenceChange(idx, "stgOutputQty", e.target.value)} />
                            <Form.Select value={s.stgOutputUnit} onChange={(e) => handleSequenceChange(idx, "stgOutputUnit", e.target.value)}>
                              {units.map(u => <option key={u.code || u.unitCode || u.id} value={u.code ?? u.unitCode ?? u.id}>{(u.code ?? u.unitCode ?? u.id) + (u.description ? ` - ${u.description}` : "")}</option>)}
                            </Form.Select>
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                ))
              )}
            </div>

            {/* Labour */}
            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Labour</h6>
                <div><Button size="sm" onClick={addLabour}>+ Add Labour</Button></div>
              </div>

              {editableLabour.length === 0 ? (
                <div className="text-muted">No labour entries.</div>
              ) : (
                editableLabour.map((l, idx) => (
                  <Row key={`${l.productionLabourType || "lab"}-${idx}`} className="align-items-end mb-2">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Labour Type</Form.Label>
                        <Form.Select value={l.productionLabourType} onChange={(e) => handleLabourChange(idx, "productionLabourType", e.target.value)}>
                          {LABOUR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Duration</Form.Label>
                        <Form.Control type="number" value={l.approxLabourDuration} onChange={(e) => handleLabourChange(idx, "approxLabourDuration", e.target.value)} />
                      </Form.Group>
                    </Col>

                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Unit</Form.Label>
                        <Form.Select value={l.approxDurationUnit} onChange={(e) => handleLabourChange(idx, "approxDurationUnit", e.target.value)}>
                          {DURATION_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Assign to Subprocess</Form.Label>
                        <Form.Select value={l.productionSubprocessId || ""} onChange={(e) => assignLabourToSubprocess(idx, e.target.value)}>
                          <option value="">-- Unassigned --</option>
                          {editableSequence.map((s, si) => (
                            <option key={si} value={s.productionSubprocessId}>{getProcessLabel(s.productionProcessId)} / {getSubprocessLabel(s.productionSubprocessId)}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={2} className="text-end">
                      <Button variant="outline-danger" onClick={() => removeLabour(idx)}>-</Button>
                    </Col>
                  </Row>
                ))
              )}
            </div>

            {/* BOM */}
            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">BOM</h6>
                <div><Button size="sm" onClick={addBOM}>+ Add BOM</Button></div>
              </div>

              {editableBOM.length === 0 ? (
                <div className="text-muted">No BOM entries.</div>
              ) : (
                editableBOM.map((b, idx) => (
                  <div key={`${b.bomId || "bom"}-${idx}`} className="mb-2 p-2 border rounded">
                    <Row className="align-items-end">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Component Item</Form.Label>
                          <Form.Select
                            value={b.componentItemCodeId || ""}
                            onChange={(e) => onBOMComponentSelect(idx, e.target.value)}
                            disabled={fetchingLookups}
                          >
                            <option value="">Select Component (RA...)</option>
                            {itemsLookup.map(it => (
                              <option key={it.itemCodeId} value={it.itemCodeId}>
                                {it.itemCode} - {it.itemName}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Assign Subprocess</Form.Label>
                          <div className="d-flex gap-1">
                            <Form.Select value={b.productionSubprocessId || ""} onChange={(e) => assignBOMToSubprocess(idx, e.target.value)}>
                              <option value="">-- Unassigned --</option>
                              {editableSequence.map((s, si) => (
                                <option key={si} value={s.productionSubprocessId}>{getProcessLabel(s.productionProcessId)} / {getSubprocessLabel(s.productionSubprocessId)}</option>
                              ))}
                            </Form.Select>
                          </div>
                        </Form.Group>
                      </Col>

                      <Col md={2} className="text-end">
                        <Button variant="outline-danger" onClick={() => removeBOM(idx)}>-</Button>
                      </Col>
                    </Row>

                    <Row className="mt-2">
                      <Col md={12}>
                        {b.bomLoading ? (
                          <div><Spinner animation="border" size="sm" /> Loading BOM...</div>
                        ) : b.bomError ? (
                          <div className="text-danger small">{b.bomError}</div>
                        ) : b.bomId ? (
                          <div className="small"><strong>Loaded BOM ID:</strong> {b.bomId}</div>
                        ) : (
                          <div className="small text-muted">No BOM loaded for selected component.</div>
                        )}
                      </Col>
                    </Row>
                  </div>
                ))
              )}
            </div>

          </Col>
        </Row>

      </Container>
    </div>
  );
}
